/**
 * vendorLicensesApi — RTK Query slice cho /vendors/{code}/licenses/*.
 * Tag enhanced: 'VendorLicense'.
 *
 * Endpoints (Sprint 5):
 *   GET    /vendors/{code}/licenses/list
 *   POST   /vendors/{code}/licenses/create
 *   PUT    /vendors/{code}/licenses/{license_id}
 *   DELETE /vendors/{code}/licenses/{license_id}
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  MessageResponse,
  VendorLicenseCreateIn,
  VendorLicenseOut,
  VendorLicenseUpdateIn,
} from '@/types/api'

const vendorLicEnhanced = baseApi.enhanceEndpoints({
  addTagTypes: ['VendorLicense'],
})

export const vendorLicensesApi = vendorLicEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listVendorLicenses: b.query<DataResponse<VendorLicenseOut[]>, { code: string }>({
      query: ({ code }) => `/vendors/${encodeURIComponent(code)}/licenses/list`,
      providesTags: (_r, _e, arg) => [
        { type: 'VendorLicense', id: `LIST-${arg.code}` },
      ],
    }),
    createVendorLicense: b.mutation<
      DataResponse<VendorLicenseOut>,
      { code: string; body: VendorLicenseCreateIn }
    >({
      query: ({ code, body }) => ({
        url: `/vendors/${encodeURIComponent(code)}/licenses/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'VendorLicense', id: `LIST-${arg.code}` },
      ],
    }),
    updateVendorLicense: b.mutation<
      DataResponse<VendorLicenseOut>,
      { code: string; license_id: number; body: VendorLicenseUpdateIn }
    >({
      query: ({ code, license_id, body }) => ({
        url: `/vendors/${encodeURIComponent(code)}/licenses/${license_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'VendorLicense', id: `LIST-${arg.code}` },
      ],
    }),
    deleteVendorLicense: b.mutation<
      MessageResponse,
      { code: string; license_id: number }
    >({
      query: ({ code, license_id }) => ({
        url: `/vendors/${encodeURIComponent(code)}/licenses/${license_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'VendorLicense', id: `LIST-${arg.code}` },
      ],
    }),
  }),
})

export const {
  useListVendorLicensesQuery,
  useCreateVendorLicenseMutation,
  useUpdateVendorLicenseMutation,
  useDeleteVendorLicenseMutation,
} = vendorLicensesApi
